import { Badge } from "./badge";
import { Button } from "./button";
import { Card, CardContent } from "./card";
import { Input } from "./input";
import { Label } from "./label";
import { RadioGroup, RadioGroupItem } from "./radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";
import { Separator } from "./separator";
import { Textarea } from "./textarea";
import { cn } from "../../lib/utils";
import {
  PROJECT_BUDGET_OPTIONS,
  START_PROJECT_SERVICE_PLANS,
} from "../../data/pricingTiers";
import { BadgeCheck, Check } from "lucide-react";

const HIGHLIGHTS = [
  {
    id: 1,
    feature: "Response within 24 hours",
  },
  {
    id: 2,
    feature: "Clear scope, timeline, and estimate",
  },
  {
    id: 3,
    feature: "No pressure—say yes only when it fits",
  },
];

const TIMELINE_OPTIONS = [
  { value: "asap", label: "ASAP" },
  { value: "2-4-weeks", label: "2 – 4 weeks" },
  { value: "1-2-months", label: "1 – 2 months" },
  { value: "not-sure", label: "Not sure" },
];

const CONTACT_EMAIL = "guevaramauricio754@gmail.com";
const CONTACT_PHONE_DISPLAY = "(336) 569-9022";
const CONTACT_PHONE_TEL = "+13365699022";

/**
 * Workspace-style two-column project inquiry form (shadcn + Tailwind).
 */
export default function ProjectInquiryFormLayout({
  formData,
  onFieldChange,
  onSubmit,
  submitStatus,
  onCancel,
}) {
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(e);
  };

  const cancelClick = () => {
    if (typeof onCancel === "function") onCancel();
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const statusTone = submitStatus?.includes?.("successfully")
    ? "text-emerald-400"
    : submitStatus?.includes?.("wait") ||
        submitStatus?.includes?.("Please")
      ? "text-muted-foreground"
      : "text-red-400";

  return (
    <div className="flex justify-center px-4 py-10 sm:px-6">
      <form
        onSubmit={handleSubmit}
        className="w-full sm:mx-auto sm:max-w-7xl text-foreground"
      >
        <h3 className="text-xl font-semibold text-foreground md:text-2xl">
          Tell us about your project
        </h3>
        <p className="mt-1 max-w-2xl text-sm leading-relaxed text-muted-foreground">
          Fill in what you know—we&apos;ll come back with a clear plan,
          timeline, and next steps.
        </p>

        <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-7 space-y-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-6 sm:gap-4">
              <div className="col-span-full sm:col-span-3">
                <Label htmlFor="name" className="font-medium">
                  Full name<span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  name="name"
                  autoComplete="name"
                  required
                  value={formData.name}
                  onChange={(e) => onFieldChange("name", e.target.value)}
                  className="mt-2"
                  placeholder="Jane Doe"
                />
              </div>
              <div className="col-span-full sm:col-span-3">
                <Label htmlFor="email" className="font-medium">
                  Email<span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={(e) => onFieldChange("email", e.target.value)}
                  className="mt-2"
                  placeholder="you@company.com"
                />
              </div>
              <div className="col-span-full">
                <Label htmlFor="website" className="font-medium">
                  Business / website{" "}
                  <span className="font-normal text-muted-foreground">
                    (optional)
                  </span>
                </Label>
                <Input
                  id="website"
                  name="website"
                  type="url"
                  autoComplete="url"
                  value={formData.website}
                  onChange={(e) => onFieldChange("website", e.target.value)}
                  className="mt-2"
                  placeholder="https://"
                />
              </div>
              <div className="col-span-full sm:col-span-3">
                <Label htmlFor="budget" className="font-medium">
                  Budget<span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.budget || undefined}
                  onValueChange={(v) => onFieldChange("budget", v)}
                >
                  <SelectTrigger id="budget" name="budget" className="mt-2">
                    <SelectValue placeholder="Select budget range" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROJECT_BUDGET_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-full sm:col-span-3">
                <Label htmlFor="timeline" className="font-medium">
                  Timeline<span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.timeline || undefined}
                  onValueChange={(v) => onFieldChange("timeline", v)}
                >
                  <SelectTrigger id="timeline" name="timeline" className="mt-2">
                    <SelectValue placeholder="When do you need this?" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMELINE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-full">
                <Label htmlFor="projectDetails" className="font-medium">
                  Tell us what you&apos;re building
                  <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="projectDetails"
                  name="projectDetails"
                  required
                  rows={5}
                  value={formData.projectDetails}
                  onChange={(e) =>
                    onFieldChange("projectDetails", e.target.value)
                  }
                  className="mt-2"
                  placeholder="Goals, audience, pages, integrations, inspiration links…"
                />
              </div>
            </div>

            <h4 className="mt-2 font-medium text-foreground">
              Service focus<span className="text-red-500">*</span>
            </h4>
            <RadioGroup
              value={formData.projectType || undefined}
              onValueChange={(v) => onFieldChange("projectType", v)}
              className="mt-4 space-y-4"
            >
              {START_PROJECT_SERVICE_PLANS.map((plan) => (
                <label
                  key={plan.value}
                  htmlFor={plan.value}
                  className={cn(
                    "relative block cursor-pointer rounded-md border bg-card transition",
                    formData.projectType === plan.value
                      ? "border-primary/40 ring-2 ring-primary/25"
                      : "border-border hover:border-border/80",
                  )}
                >
                  <div className="flex items-start space-x-4 px-5 py-4 sm:px-6">
                    <div className="mt-1 flex h-4 w-4 shrink-0 items-center justify-center">
                      <RadioGroupItem value={plan.value} id={plan.value} />
                    </div>
                    <div className="w-full min-w-0">
                      <p className="leading-6">
                        <span className="font-semibold text-foreground">
                          {plan.name}
                        </span>
                        {plan.isRecommended ? (
                          <Badge variant="secondary" className="ml-2">
                            Most recommended
                          </Badge>
                        ) : null}
                      </p>
                      <ul className="mt-2 space-y-1">
                        {plan.features.map((line) => (
                          <li
                            key={line}
                            className="flex items-start gap-2 text-sm text-muted-foreground"
                          >
                            <Check
                              className="mt-0.5 h-4 w-4 shrink-0 text-primary"
                              aria-hidden
                            />
                            <span>{line}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="flex justify-end rounded-b-md border-t border-border bg-muted/50 px-5 py-3 sm:px-6">
                    <div className="text-sm font-medium text-muted-foreground">
                      {plan.footnote}
                    </div>
                  </div>
                </label>
              ))}
            </RadioGroup>
          </div>

          <div className="lg:col-span-5">
            <Card className="border-border bg-muted/40">
              <CardContent className="space-y-4 p-6">
                <h4 className="text-sm font-semibold text-foreground">
                  Why start with one message?
                </h4>
                <p className="text-sm leading-6 text-muted-foreground">
                  We align on outcomes first—then propose a phased build that fits
                  your budget and runway. Expect plain language and a roadmap
                  you can share internally.
                </p>
                <ul className="space-y-1">
                  {HIGHLIGHTS.map((item) => (
                    <li
                      key={item.id}
                      className="flex items-center gap-2 py-1.5 text-foreground"
                    >
                      <BadgeCheck className="h-5 w-5 shrink-0 text-primary" />
                      <span className="text-sm">{item.feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="border-t border-border pt-4 space-y-3">
                  <div>
                    <p className="mb-1 text-xs font-medium text-muted-foreground">
                      Email
                    </p>
                    <a
                      href={`mailto:${CONTACT_EMAIL}`}
                      className="break-all text-sm text-primary underline-offset-4 hover:underline"
                    >
                      {CONTACT_EMAIL}
                    </a>
                  </div>
                  <div>
                    <p className="mb-1 text-xs font-medium text-muted-foreground">
                      Phone
                    </p>
                    <a
                      href={`tel:${CONTACT_PHONE_TEL}`}
                      className="text-sm text-primary underline-offset-4 hover:underline"
                    >
                      {CONTACT_PHONE_DISPLAY}
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <Separator className="my-10 bg-border" />

        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="wantExamples"
              name="wantExamples"
              checked={formData.wantExamples}
              onChange={(e) =>
                onFieldChange("wantExamples", e.target.checked)
              }
              className="size-4 cursor-pointer rounded border border-input accent-primary bg-background ring-offset-background"
            />
            <Label
              htmlFor="wantExamples"
              className="cursor-pointer font-normal text-muted-foreground"
            >
              Want to see examples of similar work?
            </Label>
          </div>
          <div className="flex items-center justify-end gap-4">
            <Button type="button" variant="ghost" onClick={cancelClick}>
              Cancel
            </Button>
            <Button type="submit">Send project request</Button>
          </div>
        </div>

        {submitStatus ? (
          <p
            role="status"
            className={cn(
              "mt-6 text-center text-sm font-medium sm:text-left",
              statusTone,
            )}
          >
            {submitStatus}
          </p>
        ) : null}
      </form>
    </div>
  );
}
